import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { holidayApi } from '../api';
import { Btn, Input, Select, EmptyState, PageHeader } from '../components/ui';

const FY_LABEL = () => {
  const now = new Date();
  const yr = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `FY ${yr}-${String(yr + 1).slice(-2)}`;
};

const TYPE_COLORS = {
  NATIONAL: { bg: '#EFF6FF', color: '#1D4ED8' },
  REGIONAL: { bg: '#FEF3C7', color: '#B45309' },
  COMPANY:  { bg: '#F0FDF4', color: '#15803D' },
};

function HolidayCard({ holiday, onDelete, isSuperAdmin, mySelectedIds, onToggle, status, maxAllowed }) {
  const tc = TYPE_COLORS[holiday.holidayType] || TYPE_COLORS.NATIONAL;
  const selected = mySelectedIds?.includes(holiday.id);
  const frozen   = status === 'FROZEN';
  // Append time to force local timezone parse (avoids UTC midnight → day-1 bug)
  const d = new Date(holiday.holidayDate + 'T00:00:00');
  const dateStr = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', weekday: 'short' });

  return (
    <div style={{
      background: selected ? '#F0FDF4' : '#fff',
      border: `1.5px solid ${selected ? '#10B981' : '#E5E7EB'}`,
      borderRadius: '12px', padding: '14px 16px',
      transition: 'all 0.15s',
      display: 'flex', alignItems: 'center', gap: '12px',
    }}>
      {/* Date block */}
      <div style={{ textAlign: 'center', minWidth: '52px', background: '#F8FAFC', borderRadius: '8px', padding: '8px 4px' }}>
        <p style={{ fontSize: '20px', fontWeight: 800, color: '#111827', lineHeight: 1, letterSpacing: '-0.03em' }}>
          {d.getDate()}
        </p>
        <p style={{ fontSize: '11px', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase' }}>
          {d.toLocaleDateString('en-IN', { month: 'short' })}
        </p>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontWeight: 600, fontSize: '14px', color: '#111827', marginBottom: '4px' }}>{holiday.name}</p>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '999px', background: tc.bg, color: tc.color }}>
            {holiday.holidayType}
          </span>
          <span style={{ fontSize: '11px', color: '#9CA3AF' }}>{d.toLocaleDateString('en-IN', { weekday: 'long' })}</span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
        {!isSuperAdmin && (
          <button
            onClick={() => !frozen && onToggle(holiday.id)}
            disabled={frozen || (!selected && mySelectedIds?.length >= maxAllowed)}
            title={frozen ? 'Selection is frozen' : selected ? 'Remove from selection' : 'Add to selection'}
            style={{
              width: '28px', height: '28px', borderRadius: '50%',
              background: selected ? '#10B981' : '#F1F5F9',
              border: `1.5px solid ${selected ? '#10B981' : '#D1D5DB'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: frozen || (!selected && mySelectedIds?.length >= maxAllowed) ? 'not-allowed' : 'pointer',
              fontSize: '14px', opacity: frozen ? 0.5 : 1,
              transition: 'all 0.15s',
            }}
          >{selected ? '✓' : '+'}</button>
        )}
        {isSuperAdmin && (
          <button onClick={() => onDelete(holiday.id)} style={{
            background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626',
            borderRadius: '6px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 600,
          }}>Delete</button>
        )}
      </div>
    </div>
  );
}

export default function Holidays() {
  const { user } = useAuth();
  const isSA = user?.role === 'SUPER_ADMIN';
  const isEmployee = user?.role === 'EMPLOYEE';

  const [holidays,   setHolidays]   = useState([]);
  const [selection,  setSelection]  = useState(null);
  const [onLeave,    setOnLeave]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [showAdd,    setShowAdd]    = useState(false);
  const [showChange, setShowChange] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [maxHolidays, setMaxHolidays] = useState(24);
  const [maxSelection, setMaxSelection] = useState(10);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [configSaving, setConfigSaving] = useState(false);
  const [changeReason, setChangeReason] = useState('');

  // Add form state
  const [newName, setNewName]   = useState('');
  const [newDate, setNewDate]   = useState('');
  const [newType, setNewType]   = useState('NATIONAL');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const isAdminOrSA = user?.role !== 'EMPLOYEE';

    // Call each API independently so one failure doesn't block the whole page
    const safe = async (fn) => { try { return await fn(); } catch { return null; } };

    const [hRes, sRes, lRes, crRes] = await Promise.all([
      safe(() => holidayApi.getHolidays()),
      !isSA ? safe(() => holidayApi.getMySelection()) : Promise.resolve(null),
      safe(() => holidayApi.getOnLeaveToday()),
      isAdminOrSA ? safe(() => holidayApi.getChangeRequests()) : Promise.resolve(null),
    ]);

    if (!hRes) {
      setError('Could not load holidays. Please try again.');
    } else {
      setHolidays(hRes.data?.data || []);
    }
    if (sRes) setSelection(sRes.data?.data || null);
    setOnLeave(lRes?.data?.data || []);
    if (crRes) setPendingRequests(crRes?.data?.data || []);

    setLoading(false);
  }, [isSA]);

  useEffect(() => { load(); }, [load]);

  const handleSaveConfig = async () => {
    setConfigSaving(true); setError('');
    try {
      await holidayApi.updateConfig({ maxNationalHolidays: maxHolidays, maxEmployeeHolidaySelections: maxSelection });
      setSuccess('Configuration saved'); setShowConfig(false); load();
    } catch (e) { setError(e.response?.data?.message || 'Failed to save config'); }
    finally { setConfigSaving(false); }
  };

  const handleRespondChange = async (id, approve) => {
    try {
      await holidayApi.respondToChange(id, approve);
      setSuccess(approve ? 'Change request approved' : 'Change request rejected');
      load();
    } catch (e) { setError(e.response?.data?.message || 'Failed'); }
  };

  const handleAdd = async () => {
    if (!newName.trim() || !newDate) { setError('Name and date are required'); return; }
    setSaving(true); setError('');
    try {
      await holidayApi.addHoliday({ name: newName.trim(), holidayDate: newDate, holidayType: newType });
      setSuccess('Holiday added'); setShowAdd(false); setNewName(''); setNewDate('');
      load();
    } catch (e) { setError(e.response?.data?.message || 'Failed to add'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this holiday? Employees who selected it will lose it.')) return;
    try {
      await holidayApi.deleteHoliday(id);
      setSuccess('Holiday deleted'); load();
    } catch (e) { setError(e.response?.data?.message || 'Failed to delete'); }
  };

  const handleToggle = (holidayId) => {
    const current = selection?.selectedHolidays?.map(h => h.id) || [];
    const isSelected = current.includes(holidayId);
    const newIds = isSelected ? current.filter(id => id !== holidayId) : [...current, holidayId];
    setSelection(prev => ({ ...prev, selectedHolidays: newIds.map(id => ({ id })), totalSelected: newIds.length }));
    // Optimistic local state — save explicitly
  };

  const mySelectedIds = selection?.selectedHolidays?.map(h => h.id) || [];
  const maxAllowed    = selection?.maxAllowed || 10;
  const isFrozen      = selection?.status === 'FROZEN';

  const handleSave = async (freeze = false) => {
    if (freeze && mySelectedIds.length !== maxAllowed) {
      setError(`You must select exactly ${maxAllowed} holidays to freeze.`); return;
    }
    setSaving(true); setError('');
    try {
      const res = await holidayApi.saveSelection({ holidayIds: mySelectedIds, freeze });
      setSelection(res.data?.data);
      setSuccess(freeze ? 'Selection frozen successfully' : 'Selection saved');
    } catch (e) { setError(e.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleRequestChange = async () => {
    if (!changeReason.trim()) { setError('Reason is required'); return; }
    setSaving(true); setError('');
    try {
      await holidayApi.requestChange({ reason: changeReason });
      setSuccess('Change request submitted'); setShowChange(false); setChangeReason(''); load();
    } catch (e) { setError(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ padding: '20px 16px', maxWidth: '760px', margin: '0 auto' }}>
      <PageHeader
        title={`Holiday Calendar — ${FY_LABEL()}`}
        subtitle={isSA ? `${holidays.length} holidays added` : `Select ${maxAllowed} holidays · ${mySelectedIds.length} selected`}
        action={isSA && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Btn size="sm" variant="ghost" onClick={() => setShowConfig(s => !s)}>⚙ Config</Btn>
            <Btn size="sm" onClick={() => setShowAdd(s => !s)}>+ Add Holiday</Btn>
          </div>
        )}
      />

      {error   && <div style={S.err}>{error}</div>}
      {success && <div style={S.suc}>{success}</div>}


      {/* SA Config panel */}
      {isSA && showConfig && (
        <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
          <p style={{ fontWeight: 700, fontSize: '14px', color: '#065F46', marginBottom: '14px' }}>Holiday Configuration</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            <div>
              <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '6px' }}>Max holidays in calendar (1–24)</label>
              <input type="number" min={1} max={24} value={maxHolidays}
                onChange={e => setMaxHolidays(Number(e.target.value))}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }} />
            </div>
            <div>
              <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '6px' }}>Max employee selections (1–24)</label>
              <input type="number" min={1} max={24} value={maxSelection}
                onChange={e => setMaxSelection(Number(e.target.value))}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Btn size="sm" onClick={handleSaveConfig} loading={configSaving}>Save Config</Btn>
            <Btn size="sm" variant="ghost" onClick={() => setShowConfig(false)}>Cancel</Btn>
          </div>
        </div>
      )}

      {/* Admin/SA: Pending change requests */}
      {(isSA || user?.role === 'ADMIN') && pendingRequests.length > 0 && (
        <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '12px', padding: '14px 16px', marginBottom: '20px' }}>
          <p style={{ fontWeight: 700, fontSize: '14px', color: '#B45309', marginBottom: '10px' }}>
            ⚠ Pending Change Requests ({pendingRequests.length})
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {pendingRequests.map(req => (
              <div key={req.id} style={{ background: '#fff', border: '1px solid #FDE68A', borderRadius: '10px', padding: '12px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap' }}>
                  <div>
                    <p style={{ fontWeight: 600, fontSize: '13px', color: '#111827' }}>
                      {req.employeeName || 'Employee'} ({req.employeeCode}) — {req.financialYear}
                    </p>
                    <p style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>
                      {req.totalSelected} holidays selected
                    </p>
                    <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                      Reason: {req.changeRequestReason || 'No reason provided'}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    <button onClick={() => handleRespondChange(req.id, true)}
                      style={{ padding: '6px 14px', borderRadius: '8px', background: '#ECFDF5', border: '1px solid #10B981', color: '#065F46', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>
                      Approve
                    </button>
                    <button onClick={() => handleRespondChange(req.id, false)}
                      style={{ padding: '6px 14px', borderRadius: '8px', background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Who's out today */}
      {onLeave.length > 0 && (
        <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '12px', padding: '14px 16px', marginBottom: '20px' }}>
          <p style={{ fontWeight: 700, fontSize: '14px', color: '#B45309', marginBottom: '8px' }}>
            🏖 On Leave Today ({onLeave.length})
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {onLeave.map(e => (
              <span key={e.employeeId} style={{ background: '#FEF3C7', color: '#92400E', borderRadius: '999px', fontSize: '12px', fontWeight: 600, padding: '3px 10px' }}>
                {e.name} · {e.holidayName}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Add holiday form */}
      {isSA && showAdd && (
        <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
          <p style={{ fontWeight: 700, fontSize: '14px', color: '#1D4ED8', marginBottom: '14px' }}>Add National Holiday</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            <Input label="Holiday Name *" value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Diwali" />
            <Input label="Date *" type="date" value={newDate} onChange={e => setNewDate(e.target.value)} />
          </div>
          <Select label="Type" value={newType} onChange={e => setNewType(e.target.value)} style={{ marginBottom: '12px' }}>
            <option value="NATIONAL">National</option>
            <option value="REGIONAL">Regional</option>
            <option value="COMPANY">Company</option>
          </Select>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Btn size="sm" onClick={handleAdd} loading={saving}>Add Holiday</Btn>
            <Btn size="sm" variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Btn>
          </div>
        </div>
      )}

      {/* Employee: frozen banner */}
      {!isSA && isFrozen && (
        <div style={{ background: '#ECFDF5', border: '1px solid #10B981', borderRadius: '12px', padding: '14px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <p style={{ fontWeight: 700, color: '#065F46', fontSize: '14px' }}>✓ Selection frozen — {mySelectedIds.length} holidays selected</p>
            <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>To change, raise a change request for admin approval.</p>
          </div>
          {!selection?.changeRequested && (
            <Btn size="sm" variant="ghost" onClick={() => setShowChange(true)}>Request Change</Btn>
          )}
          {selection?.changeRequested && (
            <span style={{ fontSize: '12px', color: '#B45309', fontWeight: 600 }}>Change request pending admin approval</span>
          )}
        </div>
      )}

      {/* Employee: draft action bar */}
      {!isSA && !isFrozen && (
        <div style={{ background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <p style={{ fontSize: '14px', color: '#374151' }}>
            <strong>{mySelectedIds.length}</strong> of <strong>{maxAllowed}</strong> selected
            {mySelectedIds.length === maxAllowed && <span style={{ color: '#10B981', marginLeft: '6px' }}>✓ Ready to freeze</span>}
          </p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Btn size="sm" variant="ghost" onClick={() => handleSave(false)} loading={saving}>Save Draft</Btn>
            <Btn size="sm" onClick={() => handleSave(true)} loading={saving}
              style={{ opacity: mySelectedIds.length !== maxAllowed ? 0.5 : 1 }}>
              Freeze Selection
            </Btn>
          </div>
        </div>
      )}

      {/* Request change modal */}
      {showChange && (
        <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) setShowChange(false); }}>
          <div style={S.modal}>
            <p style={S.modalTitle}>Request Holiday Selection Change</p>
            <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '14px' }}>
              Your selection is frozen. Provide a reason and your admin will approve or reject the request.
            </p>
            <Input label="Reason *" value={changeReason} onChange={e => setChangeReason(e.target.value)}
              placeholder="e.g. I need to swap a holiday for a personal reason" />
            <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
              <Btn onClick={handleRequestChange} loading={saving} fullWidth>Submit Request</Btn>
              <Btn variant="ghost" onClick={() => setShowChange(false)} fullWidth>Cancel</Btn>
            </div>
          </div>
        </div>
      )}

      {/* Holiday list */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {[1,2,3,4].map(i => <div key={i} className="skeleton" style={{ height: '72px', borderRadius: '12px' }} />)}
        </div>
      ) : holidays.length === 0 ? (
        <EmptyState icon="📅" title="No holidays added yet"
          subtitle={isSA ? 'Add national holidays for the financial year.' : 'Your Super Admin hasn\'t added holidays yet.'} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {holidays.map(h => (
            <HolidayCard key={h.id} holiday={h}
              onDelete={handleDelete}
              isSuperAdmin={isSA}
              mySelectedIds={mySelectedIds}
              onToggle={handleToggle}
              status={selection?.status}
              maxAllowed={maxAllowed} />
          ))}
        </div>
      )}
    </div>
  );
}

const S = {
  err:     { padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', color: '#DC2626', fontSize: '14px', marginBottom: '16px' },
  suc:     { padding: '12px 16px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '10px', color: '#15803D', fontSize: '14px', marginBottom: '16px' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(17,24,39,.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '16px', zIndex: 200 },
  modal:   { background: '#fff', borderRadius: '20px 20px 16px 16px', padding: '24px', width: '100%', maxWidth: '480px', boxShadow: '0 -4px 40px rgba(0,0,0,.12)' },
  modalTitle: { fontSize: '17px', fontWeight: 700, color: '#111827', marginBottom: '10px', letterSpacing: '-0.02em' },
};
